import Client from 'models/Client';
import type { IntegrationType } from 'services/api/fields/admin/client';

/**
 * Display names for each integration type, in priority order.
 *
 * The declaration order is meaningful — when a client has more than one
 * integration configured (e.g. an EHR + the Kno2 overlay), the first entry
 * here that the client has wins. Source EHRs come before Kno2 because the
 * CCD content originates in the source EHR.
 */
const INTEGRATION_DISPLAY_NAMES: Record<IntegrationType, string> = {
  matrix: 'MatrixCare',
  pcc: 'PointClickCare',
  epic: 'Epic',
  kno2: 'Kno2',
};

/**
 * True if the client has at least one EHR integration configured.
 */
export function clientHasIntegration(client?: Client | null): boolean {
  return !!client && client.integrationTypes.length > 0;
}

/**
 * The client's primary integration type, following the priority order in
 * `INTEGRATION_DISPLAY_NAMES`. Returns undefined when the client has no
 * integration configured.
 */
export function primaryIntegrationType(client?: Client | null): IntegrationType | undefined {
  if (!client || client.integrationTypes.length === 0) return undefined;
  return (Object.keys(INTEGRATION_DISPLAY_NAMES) as IntegrationType[]).find((t) => client.integrationTypes.includes(t));
}

/**
 * Render-ready display name for the client's primary EHR integration.
 */
export function integrationDisplayName(client?: Client | null): string {
  const primary = primaryIntegrationType(client);
  return primary ? INTEGRATION_DISPLAY_NAMES[primary] : '';
}
